/**
 * m3 echo Frontend - Heatmap Chart Component
 *
 * Displays a heatmap visualization of prompt scores over time.
 * Rows = Prompts, Columns = Tests/Jobs, Colors = Score ranges
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  IconButton,
  useTheme,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useHeatmapData } from '../hooks/useProjects';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { useModelCatalog } from '../hooks/useModelCatalog';

interface HeatmapChartProps {
  projectId: number;
  onCellClick?: (jobId: number) => void;
}

/**
 * Get color based on score value
 */
const getScoreColor = (score: number | null): string => {
  if (score === null) return '#e0e0e0'; // Grey for no data
  if (score >= 80) return '#4caf50';    // Green (Position 1-2)
  if (score >= 50) return '#ffc107';    // Yellow (Position 3)
  if (score >= 30) return '#ff9800';    // Orange (Position 4+)
  return '#f44336';                      // Red (Not mentioned)
};

/**
 * Truncate text with ellipsis
 */
const truncateText = (text: string, maxLength: number = 25): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const HeatmapChart: React.FC<HeatmapChartProps> = ({ projectId, onCellClick }) => {
  const theme = useTheme();
  const reduceMotion = useReducedMotion();
  const { modelLabel } = useModelCatalog();
  const [selectedModel, setSelectedModel] = useState<string>('all');
  const [show7TAvg, setShow7TAvg] = useState(false);
  const [hoverRow, setHoverRow] = useState<number | null>(null);
  const [hoverCol, setHoverCol] = useState<number | null>(null);
  const [promptsExpanded, setPromptsExpanded] = useState(false);

  const handleCellEnter = useCallback((rowIdx: number, colIdx: number) => {
    setHoverRow(rowIdx);
    setHoverCol(colIdx);
  }, []);

  const handleCellLeave = useCallback(() => {
    setHoverRow(null);
    setHoverCol(null);
  }, []);

  const { data: heatmapData, isLoading, error } = useHeatmapData(
    projectId,
    selectedModel === 'all' ? undefined : selectedModel
  );

  // Create lookup map for O(1) cell access
  const cellLookup = useMemo(() => {
    if (!heatmapData) return new Map<string, number | null>();
    const map = new Map<string, number | null>();
    for (const cell of heatmapData.data) {
      const key = `${cell.prompt_id}:${cell.job_id}`;
      map.set(key, cell.score);
    }
    return map;
  }, [heatmapData]);

  // Compute 7T averaged columns and cell lookup
  const { columns7TAvg, cellLookup7TAvg } = useMemo(() => {
    if (!heatmapData || heatmapData.columns.length < 7) {
      return { columns7TAvg: heatmapData?.columns || [], cellLookup7TAvg: cellLookup };
    }

    const cols = heatmapData.columns;
    const avgColumns: typeof cols = [];
    const avgCellMap = new Map<string, number | null>();

    for (let i = 0; i <= cols.length - 7; i += 7) {
      const chunk = cols.slice(i, i + 7);
      const startIdx = i + 1;
      const endIdx = Math.min(i + 7, cols.length);
      const groupId = `avg_${i}`;

      avgColumns.push({
        job_id: groupId as any,
        date: `${chunk[0].label} - ${chunk[chunk.length - 1].label}`,
        label: `#${startIdx}-${endIdx}`,
        created_at: chunk[0].created_at,
      });

      for (const prompt of heatmapData.prompts) {
        const scores: number[] = [];
        for (const col of chunk) {
          const key = `${prompt.id}:${col.job_id}`;
          const score = cellLookup.get(key);
          if (score !== null && score !== undefined) {
            scores.push(score);
          }
        }
        const avgKey = `${prompt.id}:${groupId}`;
        avgCellMap.set(avgKey, scores.length > 0
          ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
          : null
        );
      }
    }

    return { columns7TAvg: avgColumns, cellLookup7TAvg: avgCellMap };
  }, [heatmapData, cellLookup]);

  const activeColumns = show7TAvg ? columns7TAvg : (heatmapData?.columns || []);
  const activeCellLookup = show7TAvg ? cellLookup7TAvg : cellLookup;

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Score Heatmap
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Score Heatmap
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="error">
              Fehler beim Laden der Daten
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Empty state - no data or no prompts
  if (!heatmapData || heatmapData.columns.length === 0 || heatmapData.prompts.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Score Heatmap
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="text.secondary">
              Noch keine Daten vorhanden
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Tooltip title="Farbcodierte Darstellung der Prompt-Scores im Zeitverlauf. Grün = hoher Score, Rot = niedriger Score. Ideal um Trends und Ausreißer schnell zu erkennen." arrow>
              <Typography variant="h6" sx={{ cursor: 'help' }}>
                Score Heatmap
              </Typography>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              Prompt-Scores im Zeitverlauf
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={show7TAvg}
                  onChange={(e) => setShow7TAvg(e.target.checked)}
                  size="small"
                />
              }
              label="7T Mittelwert"
              sx={{ mr: 0 }}
            />
            {/* Model Filter Dropdown */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Modell</InputLabel>
              <Select
                value={selectedModel}
                label="Modell"
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                <MenuItem value="all">Alle Modelle</MenuItem>
                {heatmapData.models.map((model) => (
                  <MenuItem key={model} value={model}>{modelLabel(model)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Heatmap Grid */}
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `${promptsExpanded ? 'max-content' : '180px'} repeat(${activeColumns.length}, 40px)`,
              gap: '3px',
              minWidth: 180 + (activeColumns.length * 43),
            }}
          >
            {/* Header Row - Date Labels */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 0.5 }}>
              <Tooltip title={promptsExpanded ? 'Prompts einklappen' : 'Prompts aufklappen'} arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => setPromptsExpanded(prev => !prev)}
                  sx={{ p: 0.25 }}
                >
                  {promptsExpanded
                    ? <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                    : <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                  }
                </IconButton>
              </Tooltip>
            </Box>
            {activeColumns.map((column) => (
              <Tooltip
                key={column.job_id}
                title={show7TAvg ? `Mittelwert: ${column.date}` : `Test vom ${column.label}`}
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    fontSize: '10px',
                    textAlign: 'center',
                    color: theme.palette.text.secondary,
                    transform: 'rotate(-45deg)',
                    transformOrigin: 'center center',
                    height: '45px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.label}
                </Box>
              </Tooltip>
            ))}

            {/* Data Rows - One per Prompt */}
            {heatmapData.prompts.map((prompt, rowIndex) => (
              <React.Fragment key={prompt.id}>
                {/* Row Label - Prompt */}
                <Tooltip title={promptsExpanded ? '' : prompt.resolved_content} arrow placement="right">
                  <Box
                    sx={{
                      fontSize: '12px',
                      pr: 1,
                      display: 'flex',
                      alignItems: promptsExpanded ? 'flex-start' : 'center',
                      overflow: 'hidden',
                      textOverflow: promptsExpanded ? 'unset' : 'ellipsis',
                      whiteSpace: promptsExpanded ? 'normal' : 'nowrap',
                      wordBreak: promptsExpanded ? 'break-word' : undefined,
                      color: theme.palette.text.primary,
                      backgroundColor: hoverRow === rowIndex ? 'rgba(103, 139, 163, 0.06)' : 'transparent',
                      ...(!reduceMotion && {
                        animation: 'heatmapRowIn 300ms cubic-bezier(0.0,0,0.2,1) both',
                        animationDelay: `${rowIndex * 50}ms`,
                        '@keyframes heatmapRowIn': {
                          '0%': { opacity: 0, transform: 'translateX(-8px)' },
                          '100%': { opacity: 1, transform: 'translateX(0)' },
                        },
                      }),
                    }}
                  >
                    {promptsExpanded ? prompt.resolved_content : truncateText(prompt.resolved_content)}
                  </Box>
                </Tooltip>

                {/* Data Cells */}
                {activeColumns.map((column, colIndex) => {
                  const key = `${prompt.id}:${column.job_id}`;
                  const score = activeCellLookup.get(key) ?? null;
                  const isHighlighted = hoverRow === rowIndex || hoverCol === colIndex;

                  return (
                    <Tooltip
                      key={key}
                      title={
                        score !== null
                          ? `Score: ${score.toFixed(1)}`
                          : 'Keine Daten'
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        onMouseEnter={() => handleCellEnter(rowIndex, colIndex)}
                        onMouseLeave={handleCellLeave}
                        onClick={() => {
                          if (onCellClick && typeof column.job_id === 'number') {
                            onCellClick(column.job_id);
                          }
                        }}
                        sx={{
                          width: '36px',
                          height: '24px',
                          backgroundColor: getScoreColor(score),
                          borderRadius: '3px',
                          cursor: 'pointer',
                          transition: 'opacity 0.15s, transform 0.15s',
                          opacity: hoverRow !== null && !isHighlighted ? 0.4 : 1,
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                          ...(!reduceMotion && {
                            animation: 'heatmapCellIn 200ms ease both',
                            animationDelay: `${rowIndex * 50 + colIndex * 10}ms`,
                            '@keyframes heatmapCellIn': {
                              '0%': { opacity: 0 },
                              '100%': { opacity: 1 },
                            },
                          }),
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* Legend */}
        <Box display="flex" gap={2} mt={3} flexWrap="wrap" justifyContent="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#4caf50', borderRadius: '3px' }} />
            <Typography variant="caption">80-100 (Gut)</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#ffc107', borderRadius: '3px' }} />
            <Typography variant="caption">50-79 (Mittel)</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#ff9800', borderRadius: '3px' }} />
            <Typography variant="caption">30-49</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#f44336', borderRadius: '3px' }} />
            <Typography variant="caption">0-29 (Schlecht)</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#e0e0e0', borderRadius: '3px' }} />
            <Typography variant="caption">Keine Daten</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default HeatmapChart;
